import DownloadIcon from "@mui/icons-material/Download";
import HistoryIcon from "@mui/icons-material/History";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { InventoryReportRequest } from "../../../@types/DRO/InventoryHistoryReport";
import { InventoryHistoryReportApi } from "../../../api/InventoryHistoryReport/InventoryHistoryReportApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";
import { Warehouse } from "../../../@types/DRO/GetAllWarehouseResponse";
import { MEDIA_BASE_URL } from "../../../config/apiConfig";

const formatDate = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

const statusStyles: Record<InventoryReportRequest["status"], string> = {
  PENDING: "bg-amber-100 text-amber-800",
  PROCESSING: "bg-blue-100 text-blue-800",
  COMPLETED: "bg-emerald-100 text-emerald-800",
  FAILED: "bg-red-100 text-red-800",
};

const WAREHOUSE_PAGE_SIZE = 100;

const InventoryHistoryReport = () => {
  const [selectedWarehouses, setSelectedWarehouses] = useState<Warehouse[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseSearch, setWarehouseSearch] = useState("");
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const warehousePageRef = useRef(0);
  const warehouseTotalPagesRef = useRef(1);
  const warehouseLoadingRef = useRef(false);
  const warehouseSearchRef = useRef("");
  const warehouseRequestRef = useRef(0);
  const [asOfDate, setAsOfDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [reports, setReports] = useState<InventoryReportRequest[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadWarehousePage = useCallback(
    async (requestedPage: number, search: string, append: boolean) => {
      if (
        warehouseLoadingRef.current ||
        (append && requestedPage >= warehouseTotalPagesRef.current)
      ) {
        return;
      }

      warehouseLoadingRef.current = true;
      setWarehouseLoading(true);
      const requestId = ++warehouseRequestRef.current;
      try {
        const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
          requestedPage,
          WAREHOUSE_PAGE_SIZE,
          search
        );
        if (requestId !== warehouseRequestRef.current) return;

        const pageData = response.data;
        setWarehouses((current) => {
          if (!append) return pageData.data;
          const existingIds = new Set(current.map((warehouse) => warehouse.id));
          return [
            ...current,
            ...pageData.data.filter((warehouse) => !existingIds.has(warehouse.id)),
          ];
        });
        warehousePageRef.current = pageData.pageNumber;
        warehouseTotalPagesRef.current = pageData.totalPages;
      } catch {
        if (requestId === warehouseRequestRef.current) {
          setError("Unable to load warehouses.");
        }
      } finally {
        if (requestId === warehouseRequestRef.current) {
          warehouseLoadingRef.current = false;
          setWarehouseLoading(false);
        }
      }
    },
    []
  );

  const loadHistory = async (currentPage = page) => {
    setLoading(true);
    setError(null);
    try {
      const response = await InventoryHistoryReportApi.api.getReportRequests(
        currentPage,
        10
      );
      setReports(response.data.data);
      setPage(response.data.pageNumber);
      setTotalPages(response.data.totalPages);
    } catch {
      setError("Unable to load report history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      warehousePageRef.current = 0;
      warehouseTotalPagesRef.current = 1;
      setWarehouses([]);
      void loadWarehousePage(0, warehouseSearch, false);
    }, warehouseSearch ? 300 : 0);

    return () => window.clearTimeout(timeout);
  }, [loadWarehousePage, warehouseSearch]);

  const handleWarehouseListScroll = (
    event: React.UIEvent<HTMLElement>
  ) => {
    const list = event.currentTarget;
    if (list.scrollHeight - list.scrollTop - list.clientHeight < 48) {
      void loadWarehousePage(
        warehousePageRef.current + 1,
        warehouseSearchRef.current,
        true
      );
    }
  };

  useEffect(() => {
    if (showHistory) {
      void loadHistory(0);
    }
  }, [showHistory]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const response = await InventoryHistoryReportApi.api.generateReport({
        date: asOfDate,
        warehouseIds:
          selectedWarehouses.length > 0
            ? selectedWarehouses.map((warehouse) => warehouse.id)
            : undefined,
      });
      setMessage(`Report ${response.data} generation started. Open History to track it.`);
      setShowHistory(true);
      await loadHistory(0);
    } catch {
      setError("Unable to start report generation.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-full bg-[#F8FAFC] p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Typography variant="h4" component="h1" fontWeight={600}>
              Inventory History Report
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Generate an inventory snapshot and track its progress.
            </Typography>
          </div>
          <Button
            variant={showHistory ? "contained" : "outlined"}
            startIcon={<HistoryIcon />}
            onClick={() => setShowHistory((visible) => !visible)}
            sx={{ textTransform: "none" }}>
            History
          </Button>
        </div>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Generate report
          </Typography>
          <div className="flex flex-wrap items-start gap-4">
            <TextField
              label="As of date"
              type="date"
              value={asOfDate}
              onChange={(event) => setAsOfDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Autocomplete
              multiple
              options={warehouses}
              value={selectedWarehouses}
              onChange={(_, value) => setSelectedWarehouses(value)}
              inputValue={warehouseSearch}
              onInputChange={(_, value) => {
                setWarehouseSearch(value);
                warehouseSearchRef.current = value;
                warehouseRequestRef.current += 1;
                warehouseLoadingRef.current = false;
                setWarehouseLoading(false);
                setError(null);
              }}
              loading={warehouseLoading}
              loadingText="Loading warehouses..."
              filterOptions={(options) => options}
              ListboxProps={{ onScroll: handleWarehouseListScroll }}
              getOptionLabel={(warehouse) => warehouse.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label="Warehouses" placeholder="All warehouses" />
              )}
              sx={{ minWidth: 320, maxWidth: 520, flex: 1 }}
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={generating || !asOfDate}
              sx={{ minHeight: 40, textTransform: "none" }}>
              {generating ? <CircularProgress size={20} color="inherit" /> : "Generate Report"}
            </Button>
          </div>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            Leave warehouses empty to generate a pan-India report.
          </Typography>
        </Paper>

        {showHistory && (
          <Paper sx={{ p: 3 }}>
            <div className="mb-3 flex items-center justify-between">
              <Typography variant="h6" fontWeight={600}>Report history</Typography>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => void loadHistory()}
                disabled={loading}
                sx={{ textTransform: "none" }}>
                Refresh
              </Button>
            </div>
            {loading ? (
              <div className="flex justify-center py-8"><CircularProgress /></div>
            ) : reports.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                No reports have been generated yet.
              </Typography>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Report ID</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>As of date</TableCell>
                    <TableCell>Warehouses</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>File ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report.reportId}>
                      <TableCell>{report.reportId}</TableCell>
                      <TableCell>{formatDate(report.createdAt)}</TableCell>
                      <TableCell>{formatDate(report.asOfDate)}</TableCell>
                      <TableCell>{report.warehouseIds || "Pan-India"}</TableCell>
                      <TableCell>
                        <span className={`rounded px-2 py-1 text-xs font-semibold ${statusStyles[report.status]}`}>
                          {report.status}
                        </span>
                        {report.errorMessage && (
                          <Typography variant="caption" color="error" display="block">
                            {report.errorMessage}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          component="a"
                          href={
                            report.fileId
                              ? `${MEDIA_BASE_URL}/${report.fileId}`
                              : undefined
                          }
                          download
                          target="_blank"
                          rel="noreferrer"
                          variant="outlined"
                          size="small"
                          startIcon={<DownloadIcon />}
                          disabled={!report.fileId}
                          sx={{ textTransform: "none" }}>
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {totalPages > 1 && (
              <div className="mt-4 flex justify-end gap-2">
                <Button disabled={page === 0 || loading} onClick={() => void loadHistory(page - 1)}>Previous</Button>
                <Button disabled={page >= totalPages - 1 || loading} onClick={() => void loadHistory(page + 1)}>Next</Button>
              </div>
            )}
          </Paper>
        )}
      </div>
      <Snackbar open={!!message} autoHideDuration={5000} onClose={() => setMessage(null)} message={message} />
    </div>
  );
};

export default InventoryHistoryReport;
